/**
 * Upload Step Component
 *
 * First step in the create table sheet wizard.
 * Handles both file selection AND processing progress.
 *
 * States:
 * 1. File Selection - User enters name and picks file
 * 2. Processing - Shows progress while file is being processed
 * 3. Failed - Shows error with retry option
 *
 * @module features/workspace/components/CreateTableSheet/steps/UploadStep
 */

import { useRef } from 'react';
import {
  ArrowRight,
  Folder,
  CheckCircle2,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import {
  Button,
  Input,
  Progress,
} from '@loader/ui';

import type { UploadStepProps } from '../types';

import { FoldingCube, FileDropzone } from '@/components/common';
import { getUserFriendlyError } from '@/lib/fileUtils';
import { PROCESSING_STEPS } from '@/lib/uploadTypes';
import { useSmoothProgress } from '@/hooks';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface FilePickerViewProps {
  tableName: string;
  onTableNameChange: (name: string) => void;
  nameExists: boolean;
  file: File | null;
  onFileSelect: (file: File | null) => void;
  isUploading: boolean;
  onUpload: () => Promise<void>;
  folderName?: string;
  selectedSheet: string | null;
  onSheetChange: (sheet: string) => void;
}

/**
 * File picker view - initial state
 */
function FilePickerView({
  tableName,
  onTableNameChange,
  nameExists,
  file,
  onFileSelect,
  isUploading,
  onUpload,
  folderName,
  selectedSheet,
  onSheetChange,
}: FilePickerViewProps) {
  const isValid = tableName.trim() !== '' && !nameExists && file !== null;

  return (
    <>
      {/* Folder context indicator */}
      {folderName && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Folder className="h-4 w-4" />
          <span>Creating in:</span>
          <span className="font-medium text-foreground">{folderName}</span>
        </div>
      )}

      {/* Table name input */}
      <div className="space-y-2">
        <label htmlFor="table-name" className="text-sm font-medium">
          Table Name
        </label>
        <Input
          id="table-name"
          placeholder="Enter table name"
          value={tableName}
          onChange={(e) => onTableNameChange(e.target.value)}
          className={nameExists ? 'border-destructive' : ''}
          disabled={isUploading}
        />
        {nameExists && (
          <p className="text-sm text-destructive">
            This name is already taken. Please choose another.
          </p>
        )}
      </div>

      {/* File upload drop zone with integrated sheet selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Data File</label>
        <FileDropzone
          onFileSelect={onFileSelect}
          selectedFile={file}
          onClearFile={() => onFileSelect(null)}
          disabled={isUploading}
          variant="compact"
          onSheetChange={onSheetChange}
          selectedSheet={selectedSheet}
        />
      </div>

      {/* Upload button */}
      <div className="mt-auto">
        <Button
          onClick={onUpload}
          className="w-full"
          disabled={!isValid || isUploading}
        >
          {isUploading ? (
            <>
              <FoldingCube size="sm" />
              <span className="ml-2">Starting upload...</span>
            </>
          ) : (
            <>
              Upload & Process
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </>
  );
}

interface ProgressViewProps {
  fileName: string;
  progress: number;
  isCompleted: boolean;
  onNext: () => void;
  isPreparingNext: boolean;
}

/**
 * Progress view - shows processing status with smooth animations
 */
function ProgressView({ fileName, progress, isCompleted, onNext, isPreparingNext }: ProgressViewProps) {
  // Track the maximum progress seen to ensure monotonic increase
  const maxProgressRef = useRef(0);

  // Update max progress (never goes backward)
  if (progress > maxProgressRef.current) {
    maxProgressRef.current = progress;
  }

  // Smooth animation for progress display
  const displayProgress = useSmoothProgress(maxProgressRef.current);

  // Determine current step based on animated progress
  const getCurrentStep = () => {
    for (let i = PROCESSING_STEPS.length - 1; i >= 0; i--) {
      if (displayProgress >= PROCESSING_STEPS[i].threshold) {
        return i;
      }
    }
    return 0;
  };

  const currentStepIndex = getCurrentStep();

  return (
    <>
      {/* Progress content - centered */}
      <div className="flex flex-1 flex-col items-center justify-center gap-6 py-8">
        {/* Animated loader or checkmark when done */}
        {isCompleted ? (
          <div className="rounded-full bg-green-500/10 p-4">
            <CheckCircle2 className="h-12 w-12 text-green-500" />
          </div>
        ) : (
          <FoldingCube size="lg" />
        )}

        {/* Progress percentage */}
        <div className="text-center">
          <p className="text-4xl font-bold text-foreground tabular-nums">
            {displayProgress}%
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Processing {fileName}
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full max-w-xs">
          <Progress value={displayProgress} className="h-2" />
        </div>

        {/* Processing steps */}
        <div className="space-y-2">
          {PROCESSING_STEPS.map((step, index) => {
            const isStepCompleted = index < currentStepIndex || (index === currentStepIndex && displayProgress >= step.threshold);
            const isCurrent = index === currentStepIndex && displayProgress < step.threshold;

            return (
              <div key={step.id} className="flex items-center gap-3 text-sm">
                {isStepCompleted ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                ) : isCurrent ? (
                  <div className="h-4 w-4 shrink-0">
                    <div className="h-4 w-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
                  </div>
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-muted shrink-0" />
                )}
                <span className={isStepCompleted || isCurrent ? 'text-foreground' : 'text-muted-foreground'}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next button - pinned to bottom like Upload & Process button */}
      <div className="mt-auto">
        <Button onClick={onNext} className="w-full" disabled={!isCompleted || isPreparingNext}>
          {isPreparingNext ? (
            <>
              <FoldingCube size="sm" />
              <span className="ml-2">Preparing...</span>
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </>
  );
}

interface ErrorViewProps {
  errorMessage: string;
  onRetry: () => void;
}

/**
 * Error view - shows failure with retry option
 */
function ErrorView({ errorMessage, onRetry }: ErrorViewProps) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 py-8">
      {/* Error icon */}
      <div className="rounded-full bg-destructive/10 p-4">
        <XCircle className="h-12 w-12 text-destructive" />
      </div>

      {/* Error message */}
      <div className="text-center space-y-2 max-w-xs">
        <p className="text-lg font-semibold text-foreground">Upload Failed</p>
        <p className="text-sm text-muted-foreground">
          {errorMessage}
        </p>
      </div>

      {/* Retry button */}
      <Button onClick={onRetry} variant="outline" className="gap-2">
        <RotateCcw className="h-4 w-4" />
        Try Again
      </Button>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Upload step for the create table sheet
 *
 * Shows file picker initially, then progress during processing,
 * or error view if something fails.
 */
export function UploadStep({
  tableName,
  onTableNameChange,
  nameExists,
  file,
  onFileSelect,
  storedFileName,
  isUploading,
  versionStatus,
  versionProgress,
  errorMessage,
  onUpload,
  onRetry,
  folderName,
  selectedSheet,
  onSheetChange,
  onNext,
  isPreparingNext,
}: UploadStepProps) {
  // Determine which view to show
  // Show progress when:
  // 1. isUploading = true (file being sent to backend, before versionStatus is set)
  // 2. versionStatus is 'queued', 'pending' or 'processing' (backend processing)
  // 3. versionStatus is 'completed' (keep showing progress until step transitions)
  const isProcessing = versionStatus === 'queued' || versionStatus === 'pending' || versionStatus === 'processing';
  const isCompleted = versionStatus === 'completed';
  const showProgress = isUploading || isProcessing || isCompleted;
  const hasFailed = versionStatus === 'failed' || (errorMessage && !showProgress);

  // Get display file name - from File object or stored name (for resume case)
  const displayFileName = file?.name ?? storedFileName;

  // Show file picker only when not processing and not failed
  // Also need a file name to show progress (either from File object or stored)
  const showFilePicker = !showProgress && !hasFailed;

  // Progress comes from useCreateTableFlow which combines:
  // - Upload phase (0-80%): Real XHR progress from uploadWithProgress
  // - Processing phase (80-100%): Backend progress from polling
  const effectiveProgress = isCompleted ? 100 : versionProgress;

  return (
    <div className="flex flex-1 flex-col gap-6 p-4">
      {showFilePicker && (
        <FilePickerView
          tableName={tableName}
          onTableNameChange={onTableNameChange}
          nameExists={nameExists}
          file={file}
          onFileSelect={onFileSelect}
          isUploading={isUploading}
          onUpload={onUpload}
          folderName={folderName}
          selectedSheet={selectedSheet}
          onSheetChange={onSheetChange}
        />
      )}

      {showProgress && displayFileName && (
        <ProgressView
          key={displayFileName}
          fileName={displayFileName}
          progress={effectiveProgress}
          isCompleted={isCompleted}
          onNext={onNext}
          isPreparingNext={isPreparingNext}
        />
      )}

      {hasFailed && (
        <ErrorView
          errorMessage={getUserFriendlyError(errorMessage)}
          onRetry={onRetry}
        />
      )}
    </div>
  );
}
